import type React from "react"
import { type MouseEventHandler, useMemo } from "react"

import { Dropdown, Loader, Popup, type SemanticICONS, Icon as SemanticIcon } from "semantic-ui-react"
import styled from "styled-components"

export const HoverDropdown = styled(Dropdown)`
  &&&&& {
    .menu {
      position: absolute;
      right: 0;
      left: auto;
      max-width: 250px;
      overflow: hidden;
      min-width: 0;

      .item {
        overflow: hidden;
        max-width: 100%;
        text-overflow: ellipsis;
      }
    }
  }
`

export const HoverActions = styled.div<{ $width?: number }>`
  position: absolute;
  padding: 10px 10px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
  right: 100%;
  min-width: 300px;
  max-height: 100%;
  overflow: hidden;
  .hover-actions-inner {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-end;
    width: 100%;
    overflow: hidden;
  }
  ${({ $width, theme }) =>
    theme.sizes.isComputer &&
    `
    width: calc(100vw - ${$width}px - 10px);
  `}
`

export const HoverActionButton = styled.div<{ $margined?: boolean }>`
  user-select: none;
  pointer-events: auto;
  background-color: rgba(18, 19, 19, 0.9);
  ${({ onClick }) =>
    !!onClick &&
    `
    cursor: pointer;
  `}
  display: flex;
  border-radius: 5px;
  padding: 7px;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
  margin-top: ${({ $margined }) => ($margined ? "30px" : null)};
  i {
    margin-left: 3px;
    margin-bottom: 4px;
    color: #fff;
  }
  span {
    color: #fff;
    display: block;
    font-size: 19px;
  }
`

export const HoverActionContainer = styled(HoverActionButton)<{ $disabled?: boolean }>`
  ${({ $disabled }) =>
    $disabled
      ? `
    filter: contrast(0.6);
  `
      : `
      &:hover {
        opacity: 0.6;
      }
      transition-property: opacity;
      transition-duration: 0.1s;
      transition-timing-function: ease-out;
    `}
`

export const HoverActionsContentBox = styled.div<{
  $large?: boolean
  $margined?: boolean
  $isOpen?: boolean
  $noClick?: boolean
  $compressAccordions?: boolean
}>`
  max-height: 100%;
  pointer-events: auto;
  margin-top: ${({ $margined, theme }) => {
    if (theme.sizes.isMobile && $margined) {
      return "15px"
    }
    if (theme.sizes.isMobile || $margined) {
      return "30px"
    }
    return null
  }};
  ${({ $isOpen }) => ($isOpen ? `width: 100%;` : ``)}
  margin-bottom: 10px;
  ${HoverActionButton} {
    ${({ $isOpen }) =>
      $isOpen &&
      `
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
    margin-bottom: 0;
    ${({ $noClick }) =>
      $noClick &&
      `
      cursor: default;
    `}
    justify-content: space-between;
    ${({ $noClick }) =>
      !$noClick &&
      `
      i {
        cursor: pointer;
      }
    `}
  }
  .fake-icon {
    width: 16px;
  }
  ${({ $compressAccordions }) =>
    $compressAccordions &&
    `
  &&&& {
    .accordion .title,
    .accordion .content {
      padding: 0;
    }
  }
    `}
  .content-box {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    min-height: 400px;
    padding: 10px;
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
    ${({ theme, $large }) =>
      !theme.sizes.isMobile &&
      `
      max-height: ${$large ? "100%" : "500px"};
    `}
    .title {
      font-weight: 600;
      display: block;
      margin-bottom: 10px;
    }
    > .content-item {
      display: flex;
      margin-bottom: 10px;
    }
  }
`

type HoverActionProps = {
  children?: React.ReactNode
  icon?: SemanticICONS
  onClick?: MouseEventHandler
  disabled?: boolean
  margined?: boolean
  loading?: boolean
  target?: string
  rel?: string
  popupStyle?: React.CSSProperties
  popupContent?: React.ReactNode
  popupPosition?:
    | "top left"
    | "top right"
    | "bottom right"
    | "bottom left"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center"
  style?: React.CSSProperties
  href?: string
  download?: string
  as?: string
}

export const HoverAction = ({
  children,
  icon,
  onClick,
  disabled,
  margined,
  target,
  rel,
  popupStyle,
  popupContent,
  popupPosition,
  style,
  loading,
  href,
  download,
  as,
}: HoverActionProps) => {
  const HoverActionComponent = useMemo(
    () => (
      <HoverActionContainer
        onClick={disabled ? null : onClick}
        // @ts-expect-error (if as="a")
        href={href}
        target={target}
        rel={rel}
        as={as}
        download={download}
        $disabled={disabled}
        $margined={margined}
        style={style}
      >
        <span>{children}</span>
        <div style={{ marginLeft: children ? 7 : 0 }}>
          {loading ? (
            <Loader size="tiny" active inline="centered" style={{ marginLeft: 4, marginRight: 3 }} />
          ) : (
            icon && <SemanticIcon name={icon} />
          )}
        </div>
      </HoverActionContainer>
    ),
    [children, disabled, icon, margined, onClick, style, loading, href, download, as, target, rel],
  )

  if (popupContent) {
    return (
      <Popup
        trigger={HoverActionComponent}
        content={popupContent}
        style={popupStyle}
        on="hover"
        hoverable
        disabled={disabled}
        position={popupPosition}
      />
    )
  }
  return HoverActionComponent
}
